<template>
  <div class="page">
    <b-container fluid>
      <b-row class="accounts-page row-cols-2">
        <b-col class="items-col col-6 col-xl-5">
          <BigButton
            :count="usersCount"
            :title="$tc('words.user', usersCount)"
            :create-text="$t('accountsPage.createUser')"
            :see-text="$tc('accountsPage.seeUsers', usersCount)"
            :icon="'face_user'"
            :route-to="['Users', 'AddUser']"
            :loading="!isUsersListLoaded"
            data-test="users-submit"
          />
          <BigButton
            :count="organisationsCount"
            :title="$tc('words.organisation', organisationsCount)"
            :create-text="$t('accountsPage.createOrganisation')"
            :see-text="$tc('accountsPage.seeOrganisations', organisationsCount)"
            :icon="'company'"
            :route-to="['Organisations', 'AddOrganisation']"
            :loading="!isOrganisationsListLoaded"
            data-test="organisations-submit"
          />
          <BigButton
            :count="groupsCount"
            :title="$tc('words.userGroup', groupsCount)"
            :create-text="$t('accountsPage.createGroup')"
            :see-text="$tc('accountsPage.seeGroups', groupsCount)"
            :icon="'user-group'"
            :route-to="['Groups', 'AddGroup']"
            :loading="!isGroupsListLoaded"
            data-test="userGroups-submit"
          />
          <BigButton
            :count="spheresCount"
            :title="$tc('words.organisationGroup', spheresCount)"
            :create-text="$t('accountsPage.createSphere')"
            :see-text="$tc('accountsPage.seeSpheres', spheresCount)"
            :icon="'sphere_planet'"
            :route-to="['Spheres', 'AddSphere']"
            :loading="!isSpheresListLoaded"
            data-test="organisationGroups-submit"
          />
          <BigButton
            :count="jurisdictionsCount"
            :title="$tc('words.jurisdiction', jurisdictionsCount)"
            :create-text="$t('accountsPage.createJurisdiction')"
            :see-text="$tc('accountsPage.seeJurisdictions', jurisdictionsCount)"
            :icon="'territory_map_cadastre'"
            :route-to="['JurisdictionsList', 'NewJurisdiction']"
            :loading="!isJurisdictionsListLoaded"
            data-test="jurisdiction-submit"
          />
        </b-col>

        <b-col class="col-5 col-xs-4">
          <div>
            <h2>
              {{ capitalizeFirstLetter($tc('words.request', 2)) }}
            </h2>
            <AccountsDemandesList
              :loading="demandesLoading"
              :reloading="reloadingUsersDemandes"
              :count="usersDemandesCount"
            >
              <template #count>
                {{ capitalizeFirstLetter($tc('words.user', 2)) }}
              </template>
              <template #item>
                <AccountDemande
                  v-for="demande of usersDemandesList"
                  :key="demande.id"
                  :demande="demande"
                  :type="$tc('words.user')"
                  :icon="'face_user'"
                  :route-to="'UserDetail'"
                />
              </template>
              <template #empty>
                {{ $t('accounts.noAccountsTasks') }}.
              </template>
            </AccountsDemandesList>
            <AccountsDemandesList
              :loading="demandesLoading"
              :reloading="reloadingUsergroupsDemandes"
              :count="usergroupsDemandesCount"
            >
              <template #count>
                {{ capitalizeFirstLetter($tc('words.organisation', 2)) }}
              </template>
              <template #item>
                <AccountDemande
                  v-for="demande of usergroupsDemandesList"
                  :key="demande.id"
                  :demande="demande"
                  :type="demande.usergroup_type.display_name"
                  :icon="'user-group'"
                  :route-to="'OrganisationDetail'"
                />
              </template>
              <template #empty>
                {{ $t('accounts.noOrganisationTasks') }}.
              </template>
            </AccountsDemandesList>
            <AccountsDemandesList
              :loading="demandesLoading"
              :reloading="reloadingAttachmentsDemandes"
              :count="attachementsDemandesCount"
            >
              <template #count>
                {{ capitalizeFirstLetter($tc('words.attachment', 2)) }}
              </template>
              <template #item>
                <AttachmentDemande
                  v-for="demande of attachementsDemandesList"
                  :key="demande.id"
                  :demande="demande"
                  :icon="'add_plus'"
                  :route-to="'UserDetail'"
                />
              </template>
              <template #empty>
                {{ $t('accounts.noAttachmentsTasks') }}.
              </template>
            </AccountsDemandesList>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import AccountDemande from '@/app/accounts/components/AccountDemande.vue';
import AccountsDemandesList from '@/app/accounts/components/AccountsDemandesList.vue';
import AttachmentDemande from '@/app/accounts/components/AttachmentDemande.vue';
import BigButton from '@/common/components/BigButton.vue';

import { mapState, mapMutations, mapActions } from 'vuex';

export default {
  name: 'Accounts',

  components : {
    AccountDemande,
    AccountsDemandesList,
    AttachmentDemande,
    BigButton,
  },

  data() {
    return {
      isUsersListLoaded: false,
      isOrganisationsListLoaded: false,
      isGroupsListLoaded: false,
      isSpheresListLoaded: false,
      isJurisdictionsListLoaded: false,
      reloadingUsersDemandes: false,
      reloadingUsergroupsDemandes: false,
      reloadingAttachmentsDemandes: false,
      demandesLoading: false,
    };
  },

  computed: {
    ...mapState('users', [
      'usersCount',
      'usersList',
      'isUsersListSearched',
      'currentUsersFilters'
    ]),
    ...mapState('organisations', [
      'organisationsCount',
      'organisationsList',
      'isOrganisationsListSearched',
      'currentOrganisationsFilters',
    ]),
    ...mapState('jurisdiction', [
      'jurisdictionsCount',
      'jurisdictionsList'
    ]),
    ...mapState('usergroups', [
      'usergroupsList',
      'spheres',
      'spheresCount',
      'groups',
      'groupsCount',
      'isGroupsSearched',
      'isSpheresSearched'
    ]),
    ...mapState('demandes', [
      'usersDemandesCount',
      'usersDemandesList',
      'usergroupsDemandesCount',
      'usergroupsDemandesList',
      'attachementsDemandesCount',
      'attachementsDemandesList'
    ]),
    ...mapState('resources', [
      'schedulers'
    ]),

    currentUsersFilterCheck() {
      return !Array.isArray(this.currentUsersFilters.status__in)
        || this.currentUsersFilters.status__in[0] !== 2;
    },
    currentOrganisationsFilter() {
      return !Array.isArray(this.currentOrganisationsFilters.usergroup__status__in)
        || this.currentOrganisationsFilters.usergroup__status__in[0] !== 2;
    }    
  },

  created() {
    this.getConfigData();
    this.getData();
  },

  mounted() {
    this.SET_RELOAD_INTERVAL_ID({
      name: 'accounts_reload',
      interval: setInterval(() => {
        this.reloadingUsersDemandes = true;
        this.GET_USERS_DEMANDES_LIST({
          direction: '-',
          field: 'date_joined'
        })
          .then(() => {
            this.reloadingUsersDemandes = false;
          })
          .catch(() => {
            this.reloadingUsersDemandes = false;
          });
        this.reloadingUsergroupsDemandes = true;
        this.GET_USERGROUPS_DEMANDES_LIST({
          direction: '-',
          field: 'date_joined'
        })
          .then(() => {
            this.reloadingUsergroupsDemandes = false;
          })
          .catch(() => {
            this.reloadingUsergroupsDemandes = false;
          });
        this.reloadingAttachmentsDemandes = true;
        this.GET_ATTACHMENT_DEMANDES_LIST()
          .then(() => {
            this.reloadingAttachmentsDemandes = false;
          })
          .catch(() => {
            this.reloadingAttachmentsDemandes = false;
          });
      }, process.env.VUE_APP_RELOAD_INTERVAL)
    });
  },

  beforeRouteLeave(to, from, next) {
    this.CLEAR_RELOAD_INTERVAL_ID('accounts_reload');
    next();
  },

  methods: {
    ...mapMutations([
      'SET_RELOAD_INTERVAL_ID',
      'CLEAR_RELOAD_INTERVAL_ID'
    ]),
    ...mapMutations('users', [
      'SET_IS_USERSLIST_SEARCHED'
    ]),
    ...mapMutations('organisations', [
      'SET_IS_ORGANISATIONSLIST_SEARCHED'
    ]),
    ...mapActions('users', [
      'GET_USERS_LIST',
      'PRESET_USERS_FILTERS',
      'PREREMOVE_USERS_FILTERS'
    ]),
    ...mapActions('organisations', [
      'GET_ORGANISATIONS_TYPES',
      'GET_ORGANISATIONS_LIST',
      'PRESET_ORGANISATIONS_FILTERS',
      'PREREMOVE_ORGANISATIONS_FILTERS',
    ]),
    ...mapActions('usergroups', [
      'GET_USERGROUPS_LIST',
      'GET_USERGROUPS_GROUPS_LIST',
      'GET_USERGROUPS_SPHERES_LIST',
      'GET_USERGROUPS_ROLES'
    ]),
    ...mapActions('demandes', [
      'GET_USERS_DEMANDES_LIST',
      'GET_USERGROUPS_DEMANDES_LIST',
      'GET_ATTACHMENT_DEMANDES_LIST'
    ]),
    ...mapActions('indexers', [
      'GET_INDEXERS'
    ]),
    ...mapActions('resources', [
      'GET_SCHEDULERS'
    ]),
    ...mapActions('jurisdiction', [
      'GET_JURISDICTIONS_LIST'
    ]),

    getConfigData() {
      this.GET_ORGANISATIONS_TYPES();
      this.GET_USERGROUPS_ROLES();
      if (this.schedulers.length === 0) {
        this.GET_SCHEDULERS();
      }
    },

    async getData() {
      this.getUsersList();
      this.getOrganisationsList();
      this.getGroupsList();
      this.getSpheresList();
      this.getUsergroupsList();
      this.getDemandes();
      this.getOrganisationsJurisdictions();
      this.GET_INDEXERS();
    },

    getUsersList() {
      if (this.usersList.length === 0 || this.isUsersListSearched || this.currentUsersFilterCheck) {
        if (this.currentUsersFilterCheck) {
          this.PREREMOVE_USERS_FILTERS({
            filter: 'status__in',
            value: this.currentUsersFilters.status__in[0]
          });
          this.PRESET_USERS_FILTERS({
            filter: 'status__in',
            value: 2
          });
        }
        this.SET_IS_USERSLIST_SEARCHED({
          isSearched: false,
          text: null
        });
        this.GET_USERS_LIST({
          direction: null,
          field: null
        }).then(() => {
          this.isUsersListLoaded = true;
        }).catch(() => {
          this.isUsersListLoaded = true;
        });
      } else {
        this.isUsersListLoaded = true;
      }
    },
    getOrganisationsList() {
      if (
        this.organisationsList.length === 0
        || this.isOrganisationsListSearched
        || this.currentOrganisationsFilter
      ) {
        if (this.currentOrganisationsFilter) {
          this.PREREMOVE_ORGANISATIONS_FILTERS({
            filter: 'usergroup__status__in',
            value: this.currentOrganisationsFilters.usergroup__status__in[0]
          });
          this.PRESET_ORGANISATIONS_FILTERS({
            filter: 'usergroup__status__in',
            value: 2
          });
        }
        this.SET_IS_ORGANISATIONSLIST_SEARCHED({
          isSearched: false,
          text: null
        });
        this.GET_ORGANISATIONS_LIST({
          direction: null,
          field: null
        }).then(() => {
          this.isOrganisationsListLoaded = true;
        }).catch(() => {
          this.isOrganisationsListLoaded = true;
        });
      } else {
        this.isOrganisationsListLoaded = true;
      }
    },
    getGroupsList() {
      if (this.groups.length === 0 || this.isGroupsSearched) {
        this.GET_USERGROUPS_GROUPS_LIST({
          direction: null,
          field: null
        }).then(() => {
          this.isGroupsListLoaded = true;
        }).catch(() => {
          this.isGroupsListLoaded = true;
        });
      } else {
        this.isGroupsListLoaded = true;
      }
    },
    getSpheresList() {
      if (this.spheres.length === 0 || this.isSpheresSearched) {
        this.GET_USERGROUPS_SPHERES_LIST({
          direction: null,
          field: null
        }).then(() => {
          this.isSpheresListLoaded = true;
        }).catch(() => {
          this.isSpheresListLoaded = true;
        });
      } else {
        this.isSpheresListLoaded = true;
      }
    },
    getUsergroupsList() {
      if (this.usergroupsList.length === 0 || this.isGroupsSearched || this.isSpheresSearched) {
        this.GET_USERGROUPS_LIST({
          direction: null,
          field: null,
          usergroupTypes: []
        });
      }
    },
    async getDemandes() {
      this.demandesLoading = true;
      await this.GET_USERS_DEMANDES_LIST({
        direction: '-',
        field: 'date_joined'
      });
      await this.GET_USERGROUPS_DEMANDES_LIST({
        direction: '-',
        field: 'date_joined'
      });
      await this.GET_ATTACHMENT_DEMANDES_LIST();
      this.demandesLoading = false;
    },
    async getOrganisationsJurisdictions() {
      try {
        await this.GET_JURISDICTIONS_LIST();
        this.isJurisdictionsListLoaded = true;
      } catch (err) {
        console.error(err);
        this.isJurisdictionsListLoaded = true;
      }
    }
  }
};
</script>

<style scoped lang="less">

.container-fluid {
  height: calc(100% - @footerHeight);
  width: 100%;
}
</style>
